import { getTenantId } from "@utils/Auth";
import { ApiClient, ApiError, ApiSchema, TenantPausedError } from "./ApiClient";
import { SuscriptionFormData, SuscriptionRelatedAttributes } from "@/formTypes/ProfileFormDataType";
import { AssetRelatedModelType, saveRelateAsset } from "./AssetsApi";
import { SuscriptionFormDataKeys } from "@/generated/SuscriptionFormDataKeys";
import { showAlert } from "@utils/Alert";
import { runOncePer } from "@utils/onceLocal";
import { ONVO_TENANT_PAYMENT_TYPE_CODE } from "@utils/Constants";
import { mirrorLogoToEmailAssets } from "@utils/emailAssets";

export type SuscriptionDataType = Partial<ApiSchema["Suscription"]["type"] & SuscriptionFormData>;
const ONE_DAY = 24 * 60 * 60 * 1000;
const ACTIVE_PROFILE_STATUS = ["active", "past_due", "trialing", "pendingPaymentMethod", "toCancel"]

const ASSET_INPUTS = {
    "logo": "suscription",
    "logo2": "suscriptionLogo2",
    "logo3": "suscriptionLogo3",
    "einvoice_p12": "suscriptionP12",
    "watermark": "suscriptionWatermark"
}

export const checkSuscritionActive = async (suscriptionId: string): Promise<boolean> => {
    const { data, errors } = await ApiClient.models.Tenant.listTenantBySuscription({
        suscriptionId
    }, {
        limit: 1
    })
    if (errors || data.length == 0) {
        showAlert("danger", "Su usuario no tiene ninguna suscripciones activa")
        return false
    } else {
        const tenant = data[0]
        if (ACTIVE_PROFILE_STATUS.includes(`${tenant.status}`)) {
            if (tenant.status == "past_due") {
                const pastDueMessage = tenant.tipoPago == ONVO_TENANT_PAYMENT_TYPE_CODE ?
                    "El pago de la suscripcion esta pendiente. Por favor, asegúrese de que la tarjeta registrada cuente con saldo suficiente para evitar interrupciones en el servicio."
                    :
                    "El pago de la suscripcion esta pendiente. Por favor, realize el pago para evitar interucciones del servicio"

                const key = `once:past_due:${suscriptionId}`;
                runOncePer(key, ONE_DAY, () => {
                    showAlert("warning", pastDueMessage)
                });
            }
            return true;
        } if (tenant.status == "paused" && tenant.tipoPago == ONVO_TENANT_PAYMENT_TYPE_CODE) {
            throw new TenantPausedError(`Su suscripcion se encuentra desactivada, para activarla por favor realize el pago de su suscripcion`, "API_ERROR");
        } else {
            showAlert("danger", "Su suscripcion se encuentra desactivada, para activarla por favor pongase en contacto con servicio al cliente")
            return false
        }
    }
};

export const fetchSuscription = async (profileId?: string): Promise<SuscriptionDataType> => {
    try {
        const selectionSet = [
            ...SuscriptionFormDataKeys.filter(k => !SuscriptionRelatedAttributes.includes(k)),
            "logoAsset.*",
            "logo2Asset.*",
            "logo3Asset.*",
            "watermarkAsset.*",
            "einvoice_p12Asset.*",
        ]

        if (profileId) {
            const { data, errors } = await ApiClient.models.Suscription.get({ id: profileId }, {
                selectionSet: selectionSet
            })
            if (errors) {
                console.error("Errores al obtener las suscripciones:", errors);
                return {};
            }
            return data;
        }

        const tenantId = await getTenantId();

        const { data, errors, nextToken: nt } = await ApiClient.models.Suscription.listSuscriptionByTenant({
            owner: tenantId
        }, {
            limit: 1,
            selectionSet: selectionSet
        })

        if (errors) {
            console.error("Errores al obtener las suscripciones:", errors);
            return {};
        }

        return data[0] || {};
    } catch (error) {
        console.error("Error en el fetch de suscripciones:", error);
        return {};
    }
};

export const createSuscription = async (input: Omit<SuscriptionDataType, 'id'>) => {
    return ApiClient.models.Suscription.create(input);
};

export const updateSuscription = async (id: any, input: Partial<Omit<SuscriptionDataType, 'id' | 'tenantId'>>): Promise<SuscriptionDataType> => {
    const tenantId = await getTenantId();
    const assetPath = `suscription/${tenantId}/`;
    let data = {
        ...input
    }

    for (const [key, value] of Object.entries(ASSET_INPUTS)) {
        const inputKey = key as keyof typeof input;
        const inputValue = input[inputKey]
        if (inputValue != null && Array.isArray(inputValue)) {
            data = await saveRelateAsset(data, inputKey, inputValue, assetPath, value as AssetRelatedModelType, id)
        }
    }

    // El logo cambio (llego como archivo, no como llave ya guardada): refrescamos la copia
    // publica que usan las plantillas de correo. Un fallo aqui no debe impedir guardar el
    // perfil; el editor vuelve a intentarlo al insertar el logo.
    if (Array.isArray(input.logo) && data.logo) {
        try {
            await mirrorLogoToEmailAssets(tenantId, `${data.logo}`)
        } catch (error) {
            console.error("No fue posible publicar el logo para las plantillas de correo:", error);
        }
    }

    const { data: savedElement, errors } = await ApiClient.models.Suscription.update(
        {
            id,
            ...data
        }
    );

    if (errors) {
        throw new ApiError(`Errores al actualizar los datos de la suscripcion`, "API_ERROR", errors);
    }

    return savedElement;
};